import React, { useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  isEmpty,
  getFloatTimeValue,
  handleFileUpload,
  buildTaskAttachmentFolder,
  buildTaskDescriptionImageFolder
} from '../../../../helpers/common';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';

import {
  createNewTask,
  createNewComment,
  updateTaskDetails,
  updateRecurringTaskDetails,
  getTaskGroupList
} from '../../../../services/taskServices';
import { dispatchProjectError, getMilestoneList } from '../../../../services/projectServices';
import { captureException } from '../../../../services/logService';
import { trackAnalyticActivity } from '../../../../services/analyticsService';
import { syncTasks } from '../../../../services/syncService';
import { getTaskTemplateList } from '../../../../services/settingServices';
import { StatusListInterface } from '../../../../interfaces/ProjectInterface';
import { CreateTaskInputInterface } from '../../../../interfaces/TaskInterface';
import { RawFile } from '../../../../mobx/interfaces/task';
import { RawProjectFile } from '../../../../mobx/interfaces/project';
import { rootStore } from '../../../../mobx/rootStore';
import { TASK_ANALYTICS } from '../../../../global/analyticsConstants';
import { ROUTES } from '../../../../routes/urls';
import { TiptapEditorRef } from '../../../../interfaces/tiptapInterface';
import { COMMENT_TYPE, FILE_TYPES, TASK_PRIORITY } from '../../../../global/constants';
import { CreateTaskPayload, TaskModalContext } from '../../../../interfaces/CreateTaskModalInterface';
import { processDescription } from '../utils/processDescription';
import { buildTemplateTaskData } from '../utils/buildTemplateTaskData';

interface UseCreateTaskSubmitOptions {
  context: TaskModalContext;
  onCancel: () => void;
  loadData?: () => void;
  onProjectChange?: (value: boolean) => void;
  inlineRef: React.RefObject<TiptapEditorRef>;
  setLoading: (v: boolean) => void;
  setFileUploading: (v: boolean) => void;
}

export function useCreateTaskSubmit({
  context,
  onCancel,
  loadData,
  onProjectChange,
  inlineRef,
  setLoading,
  setFileUploading
}: UseCreateTaskSubmitOptions) {
  const { taskStore, projectStore, projectTemplateStore } = rootStore;
  const { createTaskInput } = taskStore;
  const { project } = createTaskInput;
  const { uploadFilePercent } = projectStore;
  const { currentProjectTemplate, taskTemplateList } = projectTemplateStore;
  const params: { id: string } = useParams();
  const history = useHistory();

  const isTemplateProject = context.source === 'template';
  const isRecurringTask = context.source === 'recurring';
  const isFromShortcut = context.source === 'shortcut';
  const isFromMyTask = context.source === 'myTask';
  const isTaskDetail = context.source === 'taskDetail';
  const isFromSubList = context.source === 'taskDetail' && !!context.isFromSubList;
  const isTaskDetailPreview = context.source === 'taskDetail' && !!context.isTaskDetailPreview;

  const loadProjectData = useCallback(async () => {
    try {
      if (createTaskInput?.project?.id && !isTemplateProject) {
        const promises = [];
        if (createTaskInput?.project?.isGroupEnabled && isEmpty(project?.taskGroups)) {
          promises.push(getTaskGroupList(createTaskInput?.project?.id));
        }
        if (createTaskInput?.project?.isMilestone && isEmpty(project?.milestones)) {
          promises.push(getMilestoneList(createTaskInput?.project?.id));
        }
        promises.push(getTaskTemplateList());
        await Promise.all(promises);
      }
    } catch (error) {
      captureException(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTaskInput?.project?.id]);

  // Main submit handler
  const onCreateTask = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (isEmpty(createTaskInput?.name)) {
        dispatchProjectError('Please add title for task');
        return;
      }
      if (isEmpty(createTaskInput?.project)) {
        dispatchProjectError('Please select a project for the task at the top of the modal.');
        return;
      }

      try {
        setLoading(true);
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
        const uploadedFiles: RawFile[] = createTaskInput?.files || [];
        const descriptionData = await processDescription(
          createTaskInput?.description || '',
          buildTaskDescriptionImageFolder(createTaskInput?.project?.id)
        );

        let response;

        // Cloned tasks keep the source project's values; drop the ones the selected project doesn't allow
        const projectUsers = project?.multiAssignee ? createTaskInput?.users : createTaskInput?.users?.slice(0, 1);
        const projectMilestoneId = project?.isMilestone ? createTaskInput?.milestone?.id : undefined;
        const taskUsers = projectUsers?.map((item) => item?.id);
        const labelIdList = project?.labelsEnabled
          ? createTaskInput?.labels?.map((item: { id: string }) => item?.id)
          : [];

        // UPDATE branch
        if (!isEmpty(createTaskInput?.id)) {
          const templateTaskData = buildTemplateTaskData({
            createTaskInput,
            currentProjectTemplate,
            project,
            descriptionData,
            taskUsers: taskUsers || [],
            labelIdList: labelIdList || []
          });

          let subscribersClone = JSON.parse(JSON.stringify(createTaskInput?.subscribers));
          const taskUsersForUpdate = projectUsers
            ?.map((item) => {
              if (isEmpty(item)) {
                subscribersClone?.push(item?.id);
                return {
                  _id: item?.id,
                  CreatedBy: userDetails?.id,
                  CreatedTime: new Date().toISOString(),
                  UpdatedBy: userDetails?.id,
                  UpdatedTime: new Date().toISOString()
                };
              }
              return item;
            })
            .filter((user) => user !== undefined);

          const updatedFiles = createTaskInput?.files?.map((file) => ({
            fileType: file?.file_type,
            name: file?.name,
            href: file?.href,
            size: file?.size,
            uploadFileType: file?.uploadFileType
          }));

          taskStore.createTaskInput.setDescription(descriptionData);

          const payloadTask = {
            projectId: isTemplateProject ? currentProjectTemplate?.id : project?.id,
            name: createTaskInput?.name,
            description: descriptionData,
            dueDate: createTaskInput?.dueDate,
            dueDateUnset: isEmpty(createTaskInput?.dueDate) ? true : null,
            taskUsers: taskUsersForUpdate
              ?.map((x) => (x as { id?: string }).id)
              .filter((id): id is string => id !== undefined),
            groupId: isTemplateProject
              ? currentProjectTemplate?.isGroupEnabled && !isEmpty(createTaskInput?.groupId)
                ? createTaskInput?.groupId
                : null
              : project?.isGroupEnabled && !isEmpty(createTaskInput?.groupId)
              ? createTaskInput?.groupId
              : null,
            estimateTime: project?.isEstimate
              ? typeof createTaskInput?.estimate === 'number'
                ? createTaskInput?.estimate
                : getFloatTimeValue(createTaskInput?.estimate || '')
              : undefined,
            priority: isTemplateProject
              ? currentProjectTemplate?.priority?.enabled
                ? createTaskInput?.priority
                : null
              : project?.priority?.enabled
              ? createTaskInput?.priority
              : undefined,
            files: !isEmpty(createTaskInput?.files) ? updatedFiles : [],
            milestoneId: !isEmpty(projectMilestoneId) ? projectMilestoneId : undefined,
            milestoneIdUnset: isEmpty(projectMilestoneId) ? true : null,
            labels: labelIdList,
            statusId: isTemplateProject
              ? currentProjectTemplate?.statusEnable
                ? createTaskInput?.statusId || currentProjectTemplate?.defaultStatus?.['_id']
                : null
              : project?.statusEnable
              ? createTaskInput?.statusId || project?.defaultStatus?.['_id']
              : null
          };

          switch (true) {
            case isRecurringTask:
              response = await updateRecurringTaskDetails(createTaskInput?.id, payloadTask);
              break;
            case isTemplateProject:
              currentProjectTemplate?.upsertTasks([templateTaskData]);
              if (onCancel) onCancel();
              return;
            default:
              response = await updateTaskDetails(createTaskInput?.id, payloadTask);
              await createNewComment(createTaskInput?.id, { Type: COMMENT_TYPE.UPDATE_TASK });
          }

          trackAnalyticActivity(TASK_ANALYTICS.UPDATED);

          // CREATE branch
        } else {
          taskStore.createTaskInput.setDescription(descriptionData);
          const templateTaskData = buildTemplateTaskData({
            createTaskInput,
            currentProjectTemplate,
            project,
            descriptionData,
            taskUsers: taskUsers || [],
            labelIdList: labelIdList || []
          });

          const updatedFileName = uploadedFiles?.map((item) => ({
            name: item?.name,
            href: item?.href,
            size: item?.size,
            fileType: item?.file_type,
            uploadFileType: item?.uploadFileType
          }));

          if (isTemplateProject) {
            currentProjectTemplate?.upsertTasks([templateTaskData]);
            if (onCancel) onCancel();
            return;
          }

          const payload: CreateTaskPayload = {
            name: createTaskInput?.name,
            projectId: createTaskInput?.project?.id,
            groupId: project?.isGroupEnabled ? createTaskInput?.groupId : null,
            dueDate: createTaskInput?.dueDate,
            description: descriptionData,
            status: false,
            taskUsers,
            estimateTime: project?.isEstimate
              ? typeof createTaskInput?.estimate === 'number'
                ? createTaskInput?.estimate
                : getFloatTimeValue(createTaskInput?.estimate || '')
              : undefined,
            priority: project?.priority?.enabled ? createTaskInput?.priority : undefined,
            files: !isEmpty(uploadedFiles) ? updatedFileName : [],
            milestoneId: !isEmpty(projectMilestoneId) ? projectMilestoneId : null,
            labels: labelIdList || [],
            parentTaskId: !isEmpty(createTaskInput?.parentTaskId) ? createTaskInput?.parentTaskId : null,
            statusId: project?.statusEnable ? createTaskInput?.statusId || project?.defaultStatus?.['_id'] : undefined,
            cloneTaskId: createTaskInput?.cloneTaskId || null
          };

          if (createTaskInput?.recurringInterval) {
            payload.recurringInterval = createTaskInput.recurringInterval?.toLowerCase();
            payload.start = createTaskInput.start?.toLowerCase();
          }

          response = await createNewTask(payload as unknown as CreateTaskInputInterface);

          const eventProperties = {
            source: isFromShortcut ? 'shortcut' : isFromMyTask ? 'my task' : 'task list'
          };
          trackAnalyticActivity(TASK_ANALYTICS.CREATED, eventProperties);
        }

        // Post-submit
        if (response) {
          await syncTasks({ force: true });

          if (createTaskInput?.id && isTaskDetail && !isFromSubList) {
            rootStore.taskStore.setCurrentTask(createTaskInput?.id);
            history.push(
              ROUTES.TASK_DETAILS.replace(':id', project?.id || '').replace(':taskId', createTaskInput?.id || '')
            );
          }

          if (onCancel) onCancel();

          if (onProjectChange && project?.id !== params?.id && isTaskDetail && !isTaskDetailPreview) {
            onProjectChange(true);
          } else if (loadData) {
            loadData();
          }
        }
      } catch (e) {
        captureException(e);
      } finally {
        taskStore.createTaskInput.clear();
        setLoading(false);
      }
    },
    [
      createTaskInput,
      taskStore.createTaskInput,
      project,
      currentProjectTemplate,
      isRecurringTask,
      isTemplateProject,
      isFromShortcut,
      isFromMyTask,
      isTaskDetail,
      isFromSubList,
      isTaskDetailPreview,
      onCancel,
      params?.id,
      onProjectChange,
      history,
      loadData,
      setLoading
    ]
  );

  // Status dropdown callback
  const onUpdateStatus = useCallback(async (status: StatusListInterface, previousStatus: StatusListInterface) => {
    if (status['_id'] !== previousStatus['_id']) {
      rootStore.taskStore.createTaskInput.setStatusId(status['_id']);
    }
  }, []);

  // File upload
  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || files.length === 0) {
        dispatchProjectError('No files selected.');
        return;
      }
      try {
        setFileUploading(true);
        const fileArray = Array.from(files);
        const updatedFiles = await handleFileUpload(
          createTaskInput?.files || [],
          fileArray,
          inlineRef,
          undefined,
          buildTaskAttachmentFolder(createTaskInput?.project?.id)
        );
        const value = [...(createTaskInput?.files || []), ...(updatedFiles || [])];
        createTaskInput?.setFiles(value);
      } catch (error) {
        captureException(error);
        dispatchProjectError('Error occurred while uploading files.');
      } finally {
        setFileUploading(false);
      }
    },
    [createTaskInput, setFileUploading, inlineRef]
  );

  // Select existing project file
  const onSelectFile = useCallback(
    (item: RawProjectFile) => {
      const parts = item?.name?.split('.');
      const extension = parts?.[parts.length - 1];
      let fileUrl = '';

      if (item?.uploadFileType === FILE_TYPES.DOCS || item?.uploadFileType === FILE_TYPES.WHITEBOARD) {
        if (!isEmpty(item?.parentId) && !isEmpty(item?.id)) {
          fileUrl = `/projects/files/${params?.id}/docs/${item?.id}?folder=${item?.parentId}`;
        } else if (!isEmpty(item?.id) && isEmpty(item?.parentId)) {
          fileUrl = `/projects/files/${params?.id}/docs/${item?.id}`;
        }
      } else {
        fileUrl = item?.url || '';
      }

      const sizeInMB = item?.size / (1024 * 1024);
      const sizeValue = sizeInMB >= 1 ? `${sizeInMB.toFixed(2)} MB` : `${(item?.size / 1024).toFixed(2)} KB`;

      if (extension === 'png' || extension === 'jpeg' || extension === 'jpg') {
        inlineRef.current?.addImage(fileUrl);
      } else if (extension === 'mp4' || extension === 'mov' || extension === 'mkv' || extension === 'webm') {
        inlineRef.current?.addRecordingCard(fileUrl, item?.name);
      } else {
        const newObj: RawFile = {
          name: item?.name,
          href: fileUrl || '',
          size: sizeValue,
          file_type:
            item?.uploadFileType === FILE_TYPES.DOCS
              ? 'docs'
              : item?.uploadFileType === FILE_TYPES.WHITEBOARD
              ? 'white'
              : extension,
          isCreatedDoc: true,
          uploadFileType: Number(item?.uploadFileType)
        };
        taskStore.createTaskInput.setFiles([...(createTaskInput?.files || []), newObj]);
      }
    },
    [createTaskInput?.files, params?.id, taskStore.createTaskInput, inlineRef]
  );

  // Template apply
  const handleDocumentIconClick = useCallback(
    (templateId: string | number | undefined) => {
      if (isEmpty(createTaskInput?.project?.id)) {
        dispatchProjectError('Please select a project for the task at the top of the modal.');
        return;
      }
      const selectedTemplate = taskTemplateList?.find((template) => template?.id === templateId);
      if (!selectedTemplate) {
        dispatchProjectError('Selected template not found.');
        return;
      }
      selectedTemplate?.usersIds?.forEach((item) => {
        rootStore.taskStore.createTaskInput.addAssignee(item);
      });
      rootStore.taskStore.createTaskInput.setPriority(selectedTemplate?.priority || TASK_PRIORITY.NO_PRIORITY);
      rootStore.taskStore.createTaskInput.setDescription(selectedTemplate?.description || '');
      rootStore.taskStore.createTaskInput.setName(selectedTemplate?.name || '');
      rootStore.taskStore.createTaskInput.setFiles(selectedTemplate?.files || []);
    },
    [createTaskInput?.project?.id, taskTemplateList]
  );

  // Milestone dropdown items
  const milestoneItems = useMemo(() => {
    return project?.milestones?.map((mileStone) => ({
      id: mileStone?.id,
      label: mileStone?.milestoneName,
      onClick: () => {
        if (createTaskInput?.milestone?.id === mileStone?.id) {
          taskStore.createTaskInput.setMilestone(null);
        } else {
          taskStore.createTaskInput.setMilestone(mileStone?.id);
        }
      }
    }));
  }, [createTaskInput?.milestone?.id, project?.milestones, taskStore.createTaskInput]);

  return {
    isTemplateProject,
    isRecurringTask,
    createTaskInput,
    project,
    uploadFilePercent,
    taskTemplateList,
    milestoneItems,
    loadProjectData,
    onCreateTask,
    onUpdateStatus,
    onFileUpload,
    onSelectFile,
    handleDocumentIconClick
  };
}
